/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */

import React from "react";
import { GenericTableColumn } from "@ui-components/Table/GenericTable";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { formatDate, formatTerm, withFallback } from "kontrolEdge/helpers/formattersShappedData";

export interface DataCenterConnection {
    id: number;
    customer?: {
      name: string;
      id: string;
      chargebeeCustomerId: string
    };
    name: string;
    tag: string;
    renewDate: string;
    renewDateRaw: Date | null;
    mbpsNum: number;
    terms: string;
    currentStatus: string;
    internetCapacity: string;
    vlan: string;
    parentConnection?: {
      labelA?: string;
      idA?: number;
      labelB?: string;
      idB?: number;
    };
    parentConnectionId: string | number;
    subscriptionId: string;
    status: string;
  }  

  function toDateOrNull(s: unknown): Date | null {
    if (!s) return null;
    const d = new Date(String(s));
    return isNaN(d.getTime()) ? null : d;
  }
  function toNumberOrZero(x: unknown): number {
    if (typeof x === "number") return Number.isFinite(x) ? x : 0;
    const n = Number(String(x ?? "").replace(/,/g, ""));
    return Number.isFinite(n) ? n : 0;
  }
  const statusRank = (s?: string | null) => {
    //Order: Active > Warning > Down > Unknown
    if (s === "up") return 3;         // Active
    if (s === "warning") return 2;    // Warning
    if (s === "down") return 1;       // Down
    return 0;                         // Unknown / null / anything else
  };

export const datacenterColumns: GenericTableColumn<DataCenterConnection>[] = [
    {
    header: "DCI ID",
    accessor: "subscriptionId",
    },
    {
    header: "Parent Connection",
    accessor: "parentConnection",
    },
    {
      header: "Name",
      renderCell: (row) => {
          const nameValue = row.name;
          // If it's null, empty string, or an empty array → just dash
          if (
          nameValue == null ||
          (typeof nameValue === "string" && nameValue.trim() === "") ||
          (Array.isArray(nameValue) && nameValue.length === 0)
          ) {
              return <span className="text-semantic-text-secondary">-</span>;
          }
          // Otherwise render GenericTag
          const label = Array.isArray(nameValue)
              ? nameValue.join(" ")
              : nameValue;
          return (
              <div className="flex items-center">
              <GenericTag variant="neutral" label={label} size="small"/>
              </div>
          );
      },
      },
    {
    header: "Mbps",
    accessor: "internetCapacity",
    },
    {
    header: "VLAN",
    accessor: "vlan",
    },
    {
      header: "Datacenter Status",
      renderCell: (row) => {
        const status = row.status;
        let variant: "success"|"default"|"error"|"warning";
        let label: string;
      
          if (status === "up") {
          variant = "success";
          label = "Active";
        } else if (status === undefined || status === null) {
          variant = "default";
          label = "Unknown";
        } else if (status === "down") {
          variant = "error";
          label = "Down";
        } else {
          variant = "warning";
          label = "Warning";
        }
      return <GenericTag variant={variant} label={label} size="small"/>;
      },
      sortAccessor: (row) => statusRank(row.status),
    },
    {
    header: "Tag",
    renderCell: (row) => {
        const tagValue = row.tag;
        // If it's null, empty string, or an empty array → just dash
        if (
        tagValue == null ||
        (typeof tagValue === "string" && tagValue.trim() === "") ||
        (Array.isArray(tagValue) && tagValue.length === 0)
        ) {
            return <span className="text-semantic-text-secondary">-</span>;
        }
        // Otherwise render GenericTag
        const label = Array.isArray(tagValue)
            ? tagValue.join(" ")
            : tagValue;
        return (
            <div className="flex items-center">
            <GenericTag variant="neutral" label={label} size="small"/>
            </div>
        );
    },
    },
    {
    header: "Term",
    accessor: "terms",
    },
    {
    header: "Renew Date",
    accessor: "renewDate",
    },
];

export const shapeDatacenterData = (connections: any[]): DataCenterConnection[] => {

    return connections.map((c) => {
      // Handle missing terms safely
      let parentConnection = "-";

      if (c.kontrolEdgeLagA?.subid && c.kontrolEdgeLagB?.subid) {
        parentConnection = `[LAG A] ${c.kontrolEdgeLagA.subid}, [LAG B] ${c.kontrolEdgeLagB.subid}`;
      } else if (c.kontrolEdgeLag?.subid) {
        parentConnection = `[LAG] ${c.kontrolEdgeLag.subid}`;
      } else if (c.kontrolEdgePortA?.subid && c.kontrolEdgePortB?.subid) {
        parentConnection = `[Port A] ${c.kontrolEdgePortA.subid}, [Port B] ${c.kontrolEdgePortB.subid}`;
      } else if (c.kontrolEdgePortA?.subid) {
        parentConnection = `[Port A] ${c.kontrolEdgePortA.subid}`;
      } else if (c.kontrolEdgePortB?.subid) {
        parentConnection = `[Port B] ${c.kontrolEdgePortB.subid}`;
      } else if (c.parentConnection) {
        parentConnection = c.parentConnection;
      }

      const customer = c.customer
      ? {
        name: c.customer.name ?? "-",
        id: c.customer.id ?? "-",
        chargebeeCustomerId: c.customer.chargebeeCustomerId ?? "-"
        }
      : undefined;
      const renewDateRaw = toDateOrNull(c.renewDate);
      const mbpsNum = toNumberOrZero(c.mbps);
  
      return {
        id: c.id ?? 0,
        name: withFallback(c.name),
        customer,
        renewDateRaw,
        mbpsNum,
        renewDate: formatDate(c.renewDate),
        terms: formatTerm(c.term, c.term_unit),
        vlan: withFallback(c.vlan),
        currentStatus: withFallback(c.currentStatus),
        internetCapacity: withFallback(c.mbps),
        parentConnectionId: c.kontrolEdgePort?.id ?? "-",
        parentConnection: (() => {
          if (c.kontrolEdgeLagA || c.kontrolEdgeLagB) {
            return {
              labelA: c.kontrolEdgeLagA ? `[LAG A] ${c.kontrolEdgeLagA.subid}` : undefined,
              idA: c.kontrolEdgeLagA?.id,
              labelB: c.kontrolEdgeLagB ? `[LAG B] ${c.kontrolEdgeLagB.subid}` : undefined,
              idB: c.kontrolEdgeLagB?.id,
            };
          }
        
          if (c.kontrolEdgePortA || c.kontrolEdgePortB) {
            return {
              labelA: c.kontrolEdgePortA ? `[Port A] ${c.kontrolEdgePortA.subid}` : undefined,
              idA: c.kontrolEdgePortA?.id,
              labelB: c.kontrolEdgePortB ? `[Port B] ${c.kontrolEdgePortB.subid}` : undefined,
              idB: c.kontrolEdgePortB?.id,
            };
          }
          return undefined;
        })(),        
        tag: Array.isArray(c.tag) ? c.tag : [withFallback(c.tag)],
        subscriptionId: withFallback(c.subscriptionId),
        status: withFallback(c.status),
      };
    });
  };
  
  
    